import * as bootstrap from "bootstrap";
import { withPreviewToken, fetchWithServerErrorToast } from "../api";
import { BS5Init } from "../common";
import { copyStudentMaskAfterIdentifierChange } from "./studentIdentifierChange";

const DEFAULT_MODAL_ID = "cea-dynamic-modal";
const modalNavigation = new WeakMap();

function buildModalElement(modalId) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = `
<div class="modal fade" id="${modalId}" tabindex="-1" data-bs-backdrop="static" aria-hidden="true">
  <div class="modal-dialog modal-dialog-scrollable modal-xl">
    <div class="modal-content">
      <div class="modal-header gap-2">
        <button type="button" class="btn btn-outline-secondary btn-sm flex-shrink-0" data-dynamic-modal-back title="Wróć do poprzedniego widoku" aria-label="Wróć do poprzedniego widoku" hidden>
          <i class="bi bi-arrow-left" aria-hidden="true"></i>
        </button>
        <h5 class="modal-title text-break"></h5>
        <a class="btn btn-outline-secondary btn-sm flex-shrink-0" data-dynamic-modal-page data-modal-navigate="page" href="#" title="Przejdź do edycji" aria-label="Przejdź do edycji" hidden>
          Przejdź do edycji <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
        </a>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="d-flex justify-content-center align-items-center p-4">
          <div class="spinner-border" role="status" aria-label="Loading"></div>
        </div>
      </div>
      <div class="modal-footer" data-dynamic-modal-footer-slot hidden></div>
    </div>
  </div>
</div>
`;
  return wrapper.firstElementChild;
}

function ensureModal(modalId = DEFAULT_MODAL_ID) {
  let modalEl = document.getElementById(modalId);
  if (!modalEl) {
    modalEl = buildModalElement(modalId);
    document.body.appendChild(modalEl);
  }

  return modalEl;
}

function setModalSize(modalEl, size) {
  const dialog = modalEl.querySelector(".modal-dialog");
  if (!dialog) return;

  dialog.classList.remove("modal-sm", "modal-lg", "modal-xl");

  if (size === "sm") dialog.classList.add("modal-sm");
  if (size === "lg") dialog.classList.add("modal-lg");
  if (size === "xl") dialog.classList.add("modal-xl");
}

function isPreviewUrl(url) {
  try {
    return new URL(url, window.location.href).searchParams.get("context") === "modal-preview";
  } catch (_) {
    return false;
  }
}

function withModalPreviewContext(url) {
  const previewUrl = new URL(withPreviewToken(url), window.location.href);
  previewUrl.searchParams.set("context", "modal-preview");
  return previewUrl.toString();
}

function setModalHeaderState(modalEl, { title, subtitle = "", isPreview = false } = {}) {
  const titleEl = modalEl.querySelector(".modal-title");
  const normalizedTitle = String(title || "").trim();
  const normalizedSubtitle = String(subtitle || "").trim();

  if (titleEl) {
    titleEl.dataset.modalHeaderTitle = normalizedTitle;
    titleEl.textContent = isPreview && normalizedTitle !== "" ? `${normalizedTitle} (podgląd)` : normalizedTitle;
    if (normalizedSubtitle !== "") {
      const subtitleEl = document.createElement("small");
      subtitleEl.className = "text-muted";
      subtitleEl.textContent = normalizedSubtitle;
      // titleEl.append(" - ", subtitleEl);
      titleEl.append(subtitleEl);
    }
  }
}

function setModalPageUrl(modalEl, url, { enabled = true } = {}) {
  const pageLink = modalEl.querySelector("[data-dynamic-modal-page]");
  if (!(pageLink instanceof HTMLAnchorElement)) return;

  pageLink.hidden = true;
  pageLink.href = "#";
  if (!enabled || !url) return;

  try {
    const pageUrl = new URL(withPreviewToken(url), window.location.href);
    if (pageUrl.origin !== window.location.origin) return;
    if (pageUrl.searchParams.get("context") === "modal-preview") pageUrl.searchParams.delete("context");
    pageUrl.searchParams.set(
      "return_url",
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
    pageLink.href = pageUrl.toString();
    pageLink.hidden = false;
  } catch (_) {
    return;
  }
}

async function fetchHtml(url) {
  const res = await fetchWithServerErrorToast(withModalPreviewContext(url), {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Accept: "text/html, */*;q=0.9",
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }

  return { html: await res.text(), url: res.url || url };
}

function clearModalContent(modalEl) {
  modalEl.querySelectorAll("select.tomselected, input.tomselected").forEach((input) => input.tomselect?.destroy());
  const footer = modalEl.querySelector("[data-dynamic-modal-footer-slot]");
  if (footer) {
    footer.replaceChildren();
    footer.hidden = true;
  }
}

function applyModalBodyHtml(modalEl, html, { trigger, url } = {}) {
  const body = modalEl.querySelector(".modal-body");
  if (!body) return;

  const extractSelector = trigger?.dataset?.modalExtract || null;
  clearModalContent(modalEl);
  body.innerHTML = extractHtmlFragment(html, { selector: extractSelector });

  const footer = modalEl.querySelector("[data-dynamic-modal-footer-slot]");
  const actions = body.querySelector("[data-dynamic-modal-footer]");
  if (footer && actions) {
    footer.appendChild(actions);
    footer.hidden = false;
  }

  const metadata = body.querySelector("[data-dynamic-modal-meta]");
  setModalPageUrl(modalEl, metadata?.dataset.modalEditUrl || url, { enabled: metadata?.dataset.modalPage !== "0" });
  if (metadata instanceof HTMLElement) {
    const titleEl = modalEl.querySelector(".modal-title");
    const currentTitle =
      titleEl?.dataset.modalHeaderTitle ?? titleEl?.textContent?.replace(/\s+\(podgląd\)$/u, "") ?? "";
    setModalHeaderState(modalEl, {
      title: currentTitle,
      subtitle: titleEl?.querySelector("small")?.textContent || "",
      isPreview: metadata.dataset.modalPreview === "1",
    });
    metadata.remove();
  }

  try {
    BS5Init();
  } catch (_) {
    // ignore
  }

  const evt = new CustomEvent("cea:dynamic-modal:loaded", {
    detail: { modalEl, trigger, url },
  });
  document.dispatchEvent(evt);

  if (typeof window.CEA_AFTER_DYNAMIC_MODAL_LOAD === "function") {
    window.CEA_AFTER_DYNAMIC_MODAL_LOAD({ modalEl, trigger, url });
  }
}

function extractHtmlFragment(html, { selector } = {}) {
  const trimmed = String(html ?? "").trim();
  if (!trimmed) return "";

  // If it's a full HTML document, we want to extract a fragment (e.g. #content)
  // For partial fragments, DOMParser still works and doc.body.innerHTML will match.
  let doc;
  try {
    doc = new DOMParser().parseFromString(trimmed, "text/html");
  } catch (_) {
    return trimmed;
  }

  const preferredSelector = selector || "#content";
  const fragmentEl = doc.querySelector(preferredSelector) || doc.querySelector("#content") || doc.body;

  if (!fragmentEl) {
    return trimmed;
  }

  return fragmentEl.innerHTML;
}

function resetModalNavigation(modalEl) {
  modalNavigation.delete(modalEl);
  modalEl.querySelector("[data-dynamic-modal-back]").hidden = true;
}

async function loadIntoModal(modalEl, url, { trigger, previousView } = {}) {
  const body = modalEl.querySelector(".modal-body");
  if (!body) return;

  let navigation = modalNavigation.get(modalEl);
  if (!navigation) {
    navigation = { history: [], current: null, requestId: 0 };
    modalNavigation.set(modalEl, navigation);
  }
  const current = navigation.current;
  const targetUrl = withModalPreviewContext(url);
  if (current) {
    current.scrollTop = body.scrollTop;
    if (!previousView && current.url !== targetUrl) navigation.history.push(current);
  }
  const view = previousView || {
    url: targetUrl,
    trigger,
    title: trigger?.dataset.modalTitle || trigger?.getAttribute("title") || current?.title || "",
    subtitle: trigger?.dataset.modalDynamic ? trigger.dataset.modalSubtitle || "" : current?.subtitle || "",
    size: trigger?.dataset.modalSize || (trigger?.dataset.modalDynamic ? "xl" : current?.size) || "xl",
    scrollTop: 0,
  };
  navigation.current = view;
  const requestId = ++navigation.requestId;
  modalEl.querySelector("[data-dynamic-modal-back]").hidden = navigation.history.length === 0;
  setModalHeaderState(modalEl, { title: view.title, subtitle: view.subtitle, isPreview: isPreviewUrl(targetUrl) });
  setModalSize(modalEl, view.size);
  setModalPageUrl(modalEl, "");
  clearModalContent(modalEl);
  body.innerHTML = `
<div class="d-flex justify-content-center align-items-center p-4">
  <div class="spinner-border" role="status" aria-label="Loading"></div>
</div>
`;

  const isCurrent = () => modalNavigation.get(modalEl) === navigation && navigation.requestId === requestId;
  try {
    const loaded = await fetchHtml(url);
    if (!isCurrent()) return;
    view.url = loaded.url;
    applyModalBodyHtml(modalEl, loaded.html, { trigger: view.trigger, url: loaded.url });
    body.scrollTo({ top: view.scrollTop, behavior: "instant" });
  } catch (err) {
    if (!isCurrent()) return;
    clearModalContent(modalEl);
    const error = document.createElement("div");
    error.className = "alert alert-danger small";
    error.textContent = `Nie udało się wczytać danych. ${String(err.message || err)}`;
    body.replaceChildren(error);
  }
}

async function submitModalForm(modalEl, form, submitter) {
  const action = withPreviewToken(form.getAttribute("action") || window.location.href);
  const method = (form.getAttribute("method") || "post").toUpperCase();
  const body = modalEl.querySelector(".modal-body");

  if (form.dataset.sheetCreateNextYear === "1" && body) {
    const closeButton = modalEl.querySelector(".btn-close");
    closeButton?.setAttribute("disabled", "disabled");
    body.innerHTML = `
<div class="py-4 text-center" role="status" aria-live="polite">
  <div class="spinner-border text-primary mb-3" aria-hidden="true"></div>
  <h6 class="mb-2">Tworzenie arkusza…</h6>
  <p class="text-muted small mb-3">Kopiowanie danych i sprawdzanie przydziałów może potrwać chwilę.</p>
  <div class="progress" style="height: 0.7rem;"><div class="progress-bar progress-bar-striped progress-bar-animated w-100"></div></div>
</div>`;
  } else if (body) {
    body.classList.add("position-relative");
  }

  const formData = new FormData(form);
  if (submitter?.name) formData.append(submitter.name, submitter.value);
  const response = await fetchWithServerErrorToast(action, {
    method,
    body: formData,
    credentials: "same-origin",
    headers: {
      Accept: "application/json, text/html;q=0.9, */*;q=0.8",
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  const contentType = (response.headers.get("content-type") || "").toLowerCase();

  if (contentType.includes("application/json")) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.ok === false) {
      throw new Error(payload?.message || "Nie udało się zapisać formularza.");
    }

    if (payload?.redirect) {
      copyStudentMaskAfterIdentifierChange(payload?.identifierChange);
      window.location.href = payload.redirect;
      return;
    }

    if (payload?.reloadPage) {
      copyStudentMaskAfterIdentifierChange(payload?.identifierChange);
      window.location.reload();
      return;
    }

    const modal = bootstrap.Modal.getInstance(modalEl);
    modal?.hide();
    return;
  }

  const html = await response.text();
  applyModalBodyHtml(modalEl, html, { trigger: form, url: response.url || action });
}

function attachInternalNavigation(modalEl) {
  if (modalEl.dataset.dynamicModalNavBound === "1") return;
  modalEl.dataset.dynamicModalNavBound = "1";

  // Intercept link clicks inside modal to keep navigation in-modal
  modalEl.addEventListener("click", (e) => {
    if (e.target.closest("[data-dynamic-modal-back]")) {
      e.preventDefault();
      const previousView = modalNavigation.get(modalEl)?.history.pop();
      if (previousView) loadIntoModal(modalEl, previousView.url, { previousView });
      return;
    }
    // Dynamic triggers are handled once by the document listener.
    if (e.defaultPrevented || e.target.closest("[data-modal-dynamic]")) return;
    const a = e.target.closest("a[href]");
    if (!a) return;

    // allow explicit opt-out
    if (a.dataset.modalNavigate === "page") return;
    if (a.matches('[data-sheet-file-preview="1"]')) return;
    if (a.target && a.target !== "_self") return;

    const href = a.getAttribute("href");
    if (!href || href.startsWith("#")) return;

    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    e.preventDefault();
    loadIntoModal(modalEl, url.toString(), { trigger: a });
  });

  // Intercept GET forms inside modal (search/pagination)
  modalEl.addEventListener("submit", (e) => {
    if (e.defaultPrevented) return;
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const method = (form.getAttribute("method") || "get").toLowerCase();
    if (method !== "get") {
      if (form.dataset.modalSubmit !== "ajax") return;

      e.preventDefault();
      submitModalForm(modalEl, form, e.submitter).catch((err) => {
        const body = modalEl.querySelector(".modal-body");
        const closeButton = modalEl.querySelector(".btn-close");
        closeButton?.removeAttribute("disabled");
        if (body) {
          clearModalContent(modalEl);
          const error = document.createElement("div");
          error.className = "alert alert-danger small mb-0";
          error.textContent = `Nie udało się zapisać danych. ${String(err.message || err)}`;
          body.replaceChildren(error);
        }
      });
      return;
    }

    e.preventDefault();

    const action = form.getAttribute("action") || window.location.href;
    const url = new URL(action, window.location.href);

    const params = new URLSearchParams(new FormData(form));
    // Merge/overwrite params
    params.forEach((v, k) => url.searchParams.set(k, v));

    loadIntoModal(modalEl, url.toString(), { trigger: form });
  });
}

export function initDynamicModals({ modalId = DEFAULT_MODAL_ID } = {}) {
  if (document.documentElement.dataset.dynamicModalInit === "1") return;
  document.documentElement.dataset.dynamicModalInit = "1";

  const modalEl = ensureModal(modalId);
  attachInternalNavigation(modalEl);
  modalEl.addEventListener("hidden.bs.modal", () => resetModalNavigation(modalEl));

  document.addEventListener("click", async (e) => {
    if (e.defaultPrevented) return;
    const trigger = e.target.closest("[data-modal-dynamic]");
    if (!trigger) return;

    e.preventDefault();

    const url = trigger.dataset.modalDynamic;
    if (!url) return;

    if (!modalEl.contains(trigger)) resetModalNavigation(modalEl);

    const modal = bootstrap.Modal.getOrCreateInstance(modalEl, {
      backdrop: "static",
      focus: true,
    });

    bootstrap.Tooltip.getInstance(trigger)?.hide();
    modal.show();

    await loadIntoModal(modalEl, url, { trigger });
  });
}
